import { useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import { Loader, Alert, Modal, Toast, Empty, formatDate } from '../../components/ui.jsx';

const BLANK = {
  name: '',
  code: '',
  city: '',
  state: '',
  contactEmail: '',
  contactPhone: '',
  isActive: true,
};

export default function SuperSchools() {
  const [schools, setSchools] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .superSchools()
      .then(({ schools: list }) => setSchools(list))
      .catch((err) => setError(err.message));

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setFormError('');
    setOpen(true);
  };

  const startEdit = (school) => {
    setEditing(school);
    setForm({
      name: school.name,
      code: school.code,
      city: school.city ?? '',
      state: school.state ?? '',
      contactEmail: school.contactEmail ?? '',
      contactPhone: school.contactPhone ?? '',
      isActive: school.isActive,
    });
    setFormError('');
    setOpen(true);
  };

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError('');
    try {
      if (editing) {
        await api.updateSchool(editing.id, form);
        setToast(`Updated ${form.name}`);
      } else {
        await api.createSchool(form);
        setToast(`Added ${form.name}`);
      }
      setOpen(false);
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const approveListing = async (school) => {
    try {
      await api.updateSchool(school.id, { isListed: true });
      setToast(`${school.name} now appears in the sign-up list`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleActive = async (school) => {
    try {
      await api.updateSchool(school.id, { isActive: !school.isActive });
      setToast(`${school.name} is now ${school.isActive ? 'inactive' : 'active'}`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>🏫 Schools</h1>
          <p>
            Every school on CyberCub. Students and admins are always attached to one of these.
            Schools a student typed in during sign-up are marked <strong>Added at sign-up</strong> —
            approve one to make it selectable for everyone else.
          </p>
        </div>
        <button type="button" className="btn" onClick={startCreate}>➕ Add a school</button>
      </div>

      {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

      {!schools ? (
        <Loader label="Loading schools…" />
      ) : schools.length === 0 ? (
        <Empty
          icon="🏫"
          title="No schools yet"
          text="Add your first school, then register its admin and students."
          action={<button type="button" className="btn" onClick={startCreate}>➕ Add a school</button>}
        />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>School</th>
                <th>Code</th>
                <th>Location</th>
                <th>Contact</th>
                <th className="num">Students</th>
                <th className="num">Admins</th>
                <th>Added</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schools.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.name}</strong></td>
                  <td><code className="code-chip">{s.code}</code></td>
                  <td className="small muted">
                    {[s.city, s.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="small muted">{s.contactEmail || '—'}</td>
                  <td className="num">{s.students}</td>
                  <td className="num">{s.admins}</td>
                  <td className="small muted">{formatDate(s.createdAt)}</td>
                  <td>
                    <span className={`pill ${s.isActive ? 'pill-good' : 'pill-bad'}`}>
                      {s.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {s.isListed === false && (
                      <span className="pill pill-warn" style={{ display: 'block', marginTop: 4 }}>
                        ⏳ Added at sign-up
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="row gap-xs">
                      {s.isListed === false && (
                        <button type="button" className="btn btn-sm" onClick={() => approveListing(s)}>
                          ✓ Approve
                        </button>
                      )}
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => startEdit(s)}>
                        Edit
                      </button>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => toggleActive(s)}>
                        {s.isActive ? 'Pause' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} title={editing ? `Edit ${editing.name}` : 'Add a school'} onClose={() => setOpen(false)}>
        {formError && <Alert kind="bad" onDismiss={() => setFormError('')}>{formError}</Alert>}
        <form onSubmit={save}>
          <div className="field">
            <label htmlFor="s-name">School name</label>
            <input id="s-name" value={form.name} onChange={set('name')} required maxLength={150} />
          </div>
          <div className="form-row">
            <div className="field">
              <label htmlFor="s-code">School code</label>
              <input
                id="s-code"
                value={form.code}
                onChange={set('code')}
                required
                maxLength={30}
                placeholder="GPS"
                style={{ textTransform: 'uppercase' }}
              />
              <div className="hint">Short unique code — letters, numbers and dashes.</div>
            </div>
            <div className="field">
              <label htmlFor="s-city">City</label>
              <input id="s-city" value={form.city} onChange={set('city')} maxLength={100} />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label htmlFor="s-state">State / region</label>
              <input id="s-state" value={form.state} onChange={set('state')} maxLength={100} />
            </div>
            <div className="field">
              <label htmlFor="s-phone">Contact phone</label>
              <input id="s-phone" value={form.contactPhone} onChange={set('contactPhone')} maxLength={30} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="s-email">Contact email</label>
            <input id="s-email" type="email" value={form.contactEmail} onChange={set('contactEmail')} maxLength={150} />
          </div>
          <label className="checkline">
            <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
            School is active (students and admins here can sign in)
          </label>

          <div className="row spread gap-sm mt-md">
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button type="submit" className="btn" disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Add school'}
            </button>
          </div>
        </form>
      </Modal>

      <Toast message={toast} onDone={() => setToast('')} />
    </>
  );
}
